import { IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface KvEditorProps {
  value: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
  label?: string;
  disabled?: boolean;
}

export const KvEditor: React.FC<KvEditorProps> = ({ value, onChange, label, disabled }) => {
  const pairs = Object.entries(value);

  const updateKey = (oldKey: string, newKey: string) => {
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(value)) {
      next[k === oldKey ? newKey : k] = v;
    }
    onChange(next);
  };

  const updateValue = (key: string, newVal: string) => {
    onChange({ ...value, [key]: newVal });
  };

  const removeKey = (key: string) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };

  const addPair = () => {
    let name = 'key';
    let i = 1;
    while (Object.hasOwn(value, name)) {
      name = `key${i++}`;
    }
    onChange({ ...value, [name]: '' });
  };

  return (
    <div className="space-y-2">
      {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
      {pairs.length > 0 && (
        <div className="space-y-1.5">
          {pairs.map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <Input
                className="h-7 text-xs font-mono"
                disabled={disabled}
                value={k}
                onChange={(e) => updateKey(k, e.target.value)}
                placeholder="key"
              />
              <span className="text-muted-foreground text-xs shrink-0">:</span>
              <Input
                className="h-7 text-xs font-mono"
                disabled={disabled}
                value={v}
                onChange={(e) => updateValue(k, e.target.value)}
                placeholder="value"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                disabled={disabled}
                onClick={() => removeKey(k)}
              >
                <IconTrash size={13} />
              </Button>
            </div>
          ))}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs gap-1"
        disabled={disabled}
        onClick={addPair}
      >
        <IconPlus size={12} />
        Add entry
      </Button>
    </div>
  );
};
